/*
 * CareVego native chat widget
 * Talks to the CareVego bot REST API (POST /api/chat, /api/chat/reset,
 * GET /api/chat/poll).
 * Session id (web:<hex>) is persisted in localStorage. Exposes window.CVChat
 * so storefront CTAs can open the chat (optionally with a prefilled message).
 *
 * A human agent can take this conversation over from the CareVego agent
 * console. Their replies have no request to ride back on — the visitor isn't
 * asking for them — so once a session exists the widget polls for anything
 * queued for it. Without that poll an agent's messages are never collected and
 * the visitor sits looking at "someone will reply shortly" forever.
 */
(function () {
  const root = document.querySelector('.cvbot');
  if (!root) return;

  const API = (root.dataset.apiBase || '').replace(/\/+$/, '');
  const SESSION_KEY = root.dataset.sessionKey || 'cvchat_session';
  const GREETING = root.dataset.greeting || 'Hi! 👋 How can I help you today?';
  const TRACK_MESSAGE = root.dataset.trackMessage || 'I want to track my order';

  const el = {
    launcher: root.querySelector('.cv-chat__launcher'),
    panel: root.querySelector('.cv-chat__panel'),
    messages: root.querySelector('[data-cv-messages]'),
    prompts: root.querySelector('[data-cv-prompts]'),
    form: root.querySelector('[data-cv-form]'),
    input: root.querySelector('[data-cv-input]'),
    send: root.querySelector('[data-cv-send]'),
    close: root.querySelector('[data-cv-close]'),
    reset: root.querySelector('[data-cv-reset]'),
  };

  const POLL_MS = 4000;

  let started = false;
  let handoffNoted = false;
  let pollTimer = null;
  let unread = 0;

  const getSession = () => {
    try { return localStorage.getItem(SESSION_KEY) || null; } catch (e) { return null; }
  };
  const setSession = (id) => {
    try { if (id) localStorage.setItem(SESSION_KEY, id); } catch (e) {}
  };
  const clearSession = () => {
    try { localStorage.removeItem(SESSION_KEY); } catch (e) {}
  };

  // The transcript used to live only in the DOM, so every page navigation wiped
  // the visible conversation even though the server-side session survived.
  // Persist the messages and the open/closed state so the chat carries across pages.
  const TRANSCRIPT_KEY = SESSION_KEY + '_log';
  const OPEN_KEY = SESSION_KEY + '_open';
  const MAX_LOG = 60;

  const loadLog = () => {
    try { return JSON.parse(localStorage.getItem(TRANSCRIPT_KEY) || '[]'); } catch (e) { return []; }
  };
  const saveLog = (log) => {
    try { localStorage.setItem(TRANSCRIPT_KEY, JSON.stringify(log.slice(-MAX_LOG))); } catch (e) {}
  };
  const clearLog = () => {
    try { localStorage.removeItem(TRANSCRIPT_KEY); } catch (e) {}
  };
  const logMessage = (text, who) => {
    const l = loadLog();
    l.push({ t: text, w: who });
    saveLog(l);
  };
  const rememberOpen = (isOpen) => {
    try { localStorage.setItem(OPEN_KEY, isOpen ? '1' : '0'); } catch (e) {}
  };
  const wasOpen = () => {
    try { return localStorage.getItem(OPEN_KEY) === '1'; } catch (e) { return false; }
  };

  const escapeHtml = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  // WhatsApp-style plain text -> minimal safe HTML.
  function format(text) {
    let h = escapeHtml(text);
    h = h.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>');
    h = h.replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>');
    h = h.replace(/_([^_\n]+)_/g, '<em>$1</em>');
    h = h.replace(/^\s*[-•]\s+/gm, '• ');
    return h;
  }

  function scrollDown() {
    el.messages.scrollTop = el.messages.scrollHeight;
  }

  // persist === false when replaying stored messages, so we don't re-save them.
  function addMessage(text, who, persist) {
    const div = document.createElement('div');
    div.className = 'cv-chat__msg cv-chat__msg--' + who;
    // Everything except the visitor's own text goes through format(), which
    // escapes first and then adds links/emphasis — agents paste product links
    // too, and they should be clickable just like the bot's.
    div.innerHTML = who === 'user' ? escapeHtml(text) : format(text);
    el.messages.appendChild(div);
    scrollDown();
    if (persist !== false) logMessage(text, who);
    return div;
  }

  function showTyping() {
    const t = document.createElement('div');
    t.className = 'cv-chat__typing';
    t.innerHTML = '<span></span><span></span><span></span>';
    t.dataset.cvTyping = '1';
    el.messages.appendChild(t);
    scrollDown();
    return t;
  }

  function showError(msg) {
    const e = document.createElement('div');
    e.className = 'cv-chat__error';
    e.textContent = msg;
    el.messages.appendChild(e);
    scrollDown();
  }

  function ensureStarted() {
    if (started) return;
    started = true;
    addMessage(GREETING, 'bot');
  }

  async function send(text) {
    text = (text || '').trim();
    if (!text) return;
    if (text.length > 2000) text = text.slice(0, 2000);

    ensureStarted();
    if (el.prompts) el.prompts.style.display = 'none';
    addMessage(text, 'user');
    el.input.value = '';
    el.input.style.height = 'auto';

    const typing = showTyping();
    el.send.disabled = true;

    const body = { message: text };
    const sid = getSession();
    if (sid) body.session_id = sid;

    try {
      const res = await fetch(API + '/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      typing.remove();
      if (!res.ok) {
        if (res.status === 400) {
          // stale/invalid session — drop it and let the next message mint a fresh one
          clearSession();
          showError('Sorry, something went wrong. Please try again.');
        } else {
          showError('Our assistant is unavailable right now. Please try again shortly.');
        }
        return;
      }
      const data = await res.json();
      if (data.session_id) setSession(data.session_id);
      // There's a session now, so an agent can reach this visitor — listen.
      startPolling();
      if (data.reply && data.reply.trim()) {
        addMessage(data.reply, 'bot');
      } else {
        // Empty reply = a human agent has taken this conversation over. They may
        // already have queued a proper handover notice, so collect it now rather
        // than waiting for the next tick and printing our own line on top of it.
        await poll();
        if (!handoffNoted) {
          handoffNoted = true;
          addMessage("A member of our team is looking into this and will reply here shortly. You can keep typing — we're reading.", 'bot');
        }
      }
    } catch (e) {
      typing.remove();
      showError('Connection problem. Please check your network and try again.');
    } finally {
      el.send.disabled = false;
      el.input.focus();
    }
  }

  // ── Human agent messages ──────────────────────────────────────────────
  function setUnread(n) {
    unread = Math.max(0, n);
    root.classList.toggle('has-unread', unread > 0);
  }

  async function poll() {
    const sid = getSession();
    if (!sid) return;

    let data;
    try {
      const res = await fetch(API + '/api/chat/poll?session_id=' + encodeURIComponent(sid));
      if (!res.ok) {
        // 400 = a session id this server won't accept; stop hammering it.
        if (res.status === 400) { clearSession(); stopPolling(); }
        return;
      }
      data = await res.json();
    } catch (e) {
      // Offline or a blip. Nothing is lost: the server only drains the queue on
      // a successful read, so the next tick collects the same messages.
      return;
    }

    const messages = (data && data.messages) || [];
    if (!messages.length) return;

    ensureStarted();
    messages.forEach((m) => {
      if (!m || !m.text) return;
      // A takeover notice replaces our own "someone is looking into this" line;
      // a handback means the bot is answering again, so a later takeover should
      // announce itself afresh.
      if (m.kind === 'takeover') handoffNoted = true;
      if (m.kind === 'handback') handoffNoted = false;

      const who = m.role === 'agent' ? 'agent' : (m.role === 'system' ? 'system' : 'bot');
      addMessage(m.text, who);
    });

    if (!root.classList.contains('is-open')) {
      setUnread(unread + messages.length);
    }
  }

  function startPolling() {
    if (pollTimer) return;
    poll();
    pollTimer = setInterval(() => { if (!document.hidden) poll(); }, POLL_MS);
  }

  function stopPolling() {
    clearInterval(pollTimer);
    pollTimer = null;
  }

  async function reset() {
    const sid = getSession();
    el.messages.innerHTML = '';
    started = false;
    handoffNoted = false;
    clearLog();
    if (el.prompts) el.prompts.style.display = '';
    if (sid) {
      try { await fetch(API + '/api/chat/reset', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ session_id: sid }) }); } catch (e) {}
    }
    ensureStarted();
    el.input.focus();
  }

  function open(prefill, opts) {
    root.classList.add('is-open');
    rememberOpen(true);
    setUnread(0);
    ensureStarted();
    if (prefill) {
      el.input.value = prefill;
      el.input.style.height = 'auto';
      el.input.style.height = el.input.scrollHeight + 'px';
    }
    // Skip focus when reopening after a page load, so we don't hijack the page.
    if (!(opts && opts.silent)) setTimeout(() => el.input.focus(), 250);
  }
  function close() {
    root.classList.remove('is-open');
    rememberOpen(false);
  }
  function toggle() { root.classList.contains('is-open') ? close() : open(); }

  // Open the chat and ask about an order. By default it sends the tracking prompt
  // straight away so the bot responds; pass true to only prefill it (let the
  // visitor press send). Trigger it from a nav link pointing to …/#cv-track.
  function track(prefillOnly) {
    open(TRACK_MESSAGE);
    if (!prefillOnly) send(TRACK_MESSAGE);
  }

  // Replay any stored conversation so navigating between pages doesn't wipe it.
  function restore() {
    const log = loadLog();
    if (!log.length) return false;
    log.forEach((m) => addMessage(m.t, m.w, false));
    started = true;
    if (el.prompts && log.some((m) => m.w === 'user')) el.prompts.style.display = 'none';
    scrollDown();
    return true;
  }

  // Wire events
  el.launcher.addEventListener('click', toggle);
  if (el.close) el.close.addEventListener('click', close);
  if (el.reset) el.reset.addEventListener('click', reset);
  el.form.addEventListener('submit', (e) => { e.preventDefault(); send(el.input.value); });
  el.input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(el.input.value); }
  });
  el.input.addEventListener('input', () => {
    el.input.style.height = 'auto';
    el.input.style.height = Math.min(el.input.scrollHeight, 96) + 'px';
  });
  if (el.prompts) {
    el.prompts.querySelectorAll('.cv-chat__prompt').forEach((b) => {
      b.addEventListener('click', () => send(b.dataset.prompt || b.textContent));
    });
  }
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && root.classList.contains('is-open')) close(); });

  // Any element with [data-cv-chat-open] opens the widget; data-cv-chat-open holds an optional prefill.
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-cv-chat-open]');
    if (!t) return;
    e.preventDefault();
    open(t.getAttribute('data-cv-chat-open') || '');
  });

  // A nav link pointing to …/#cv-track opens the chat with an order-tracking
  // prompt. This uses a plain URL (which the Shopify menu editor accepts) instead
  // of a javascript: URL. Intercept the click so we never actually navigate, and
  // also handle a fresh page load that arrives on the #cv-track hash.
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href*="#cv-track"]');
    if (!a) return;
    e.preventDefault();
    track();
  });
  function trackFromHash() {
    if (/#cv-track\b/.test(location.hash)) {
      track();
      try { history.replaceState(null, '', location.pathname + location.search); } catch (e) {}
    }
  }
  window.addEventListener('hashchange', trackFromHash);

  // Carry the conversation across page loads: replay the stored transcript, and
  // reopen the panel if the visitor left it open (without stealing focus).
  restore();
  if (wasOpen()) open('', { silent: true });
  trackFromHash();

  // A returning visitor already has a session, and an agent may have replied
  // while they were on another page or had the tab closed.
  if (getSession()) startPolling();

  window.CVChat = { open, close, toggle, send, reset, track };
})();
